import React from 'react';
import Modal from '@mui/material/Modal';
import Button from 'src/component/UI/Button';
import { useTranslation } from 'src/i18n';
import './UpgradeViolationsModal.scss';

interface Violation {
  deviceTag: string;
  settingPath: string;
  value: number;
  minValue?: number;
  maxValue?: number;
}

interface MisconfigurationError {
  deviceTag: string;
  error: string;
  relatedSettings: { path: string; value: number }[];
}

interface Props {
  open: boolean;
  violations: Violation[];
  misconfigurationErrors: MisconfigurationError[];
  onConfirm: () => void;
  onCancel: () => void;
}

export default function UpgradeViolationsModal ({ open, violations, misconfigurationErrors, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  const hasIssues = violations.length > 0 || misconfigurationErrors.length > 0;

  // Group all issues by deviceTag
  const deviceTags = [...new Set([
    ...violations.map(v => v.deviceTag),
    ...misconfigurationErrors.map(e => e.deviceTag),
  ])];

  return (
    <Modal open={open} onClose={onCancel} aria-labelledby='upgrade-violations-title'>
      <div className='modal upgrade-violations-modal'>
        <h2 id='upgrade-violations-title'>
          {hasIssues ? t('upgradeDevice.violationsTitle') : t('upgradeDevice.validationPassedTitle')}
        </h2>

        <p className='upgrade-violations-subtitle'>
          {hasIssues ? t('upgradeDevice.violationsSubtitle') : t('upgradeDevice.validationPassedSubtitle')}
        </p>

        {hasIssues && deviceTags.map(tag => {
          const deviceViolations = violations.filter(v => v.deviceTag === tag);
          const deviceMisconfigs = misconfigurationErrors.filter(e => e.deviceTag === tag);
          return (
            <div key={tag} className='device-issue-card'>
              <div className='device-issue-card-header'>{tag}</div>

              {deviceViolations.length > 0 && (
                <div className='issue-section'>
                  <div className='issue-section-label'>{t('upgradeDevice.outOfRangeLabel')}</div>
                  <table className='issue-table'>
                    <thead>
                      <tr>
                        <th>{t('upgradeDevice.violationsColSetting')}</th>
                        <th className='col-right'>{t('upgradeDevice.violationsColValue')}</th>
                        <th className='col-right'>{t('upgradeDevice.violationsColAllowed')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceViolations.map((v, i) => (
                        <tr key={i}>
                          <td title={v.settingPath}>
                            {t(`gauge.${v.settingPath.split('.').pop()}`, { defaultValue: v.settingPath })}
                          </td>
                          <td className='col-right value-error'>{v.value}</td>
                          <td className='col-right'>{v.minValue ?? '–'} – {v.maxValue ?? '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {deviceMisconfigs.length > 0 && (
                <div className='issue-section'>
                  <div className='issue-section-label'>{t('upgradeDevice.conflictLabel')}</div>
                  {deviceMisconfigs.map((e, i) => (
                    <div key={i} className='misconfig-block'>
                      <div className='misconfig-reason'>
                        {t(`deviceValidationErrors.${e.error}`, { defaultValue: e.error })}
                      </div>
                      {e.relatedSettings.length > 0 && (
                        <table className='issue-table'>
                          <thead>
                            <tr>
                              <th>{t('upgradeDevice.violationsColSetting')}</th>
                              <th className='col-right'>{t('upgradeDevice.violationsColValue')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {e.relatedSettings.map((s, j) => (
                              <tr key={j}>
                                <td title={s.path}>
                                  {t(s.path, { defaultValue: t(`gauge.${s.path}`, { defaultValue: s.path }) })}
                                </td>
                                <td className='col-right value-error'>{s.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className='button-bar'>
          <Button variant='contained' onClick={onCancel}>{t('common.cancel')}</Button>
          <Button variant='contained' onClick={onConfirm} cta>
            {hasIssues ? t('upgradeDevice.reprogramAnyway') : t('upgradeDevice.reprogramNewDevice')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
